import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useImages } from '../context/ImageContext';

const CompareMedicalAlertSystemsPage: React.FC = () => {
  const { getImage } = useImages();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [openWarningIndex, setOpenWarningIndex] = useState<number | null>(null);

  // Get logo images
  const alert1Logo = getImage('logo.webp');
  const lifeAlertLogo = getImage('life-alert-logo.jpg');
  const bayAlarmLogo = getImage('bay-alarm-medical-logo.png');
  const medicalGuardianLogo = getImage('medical-guardian-logo.png');
  const greatcallLogo = getImage('greatcall-logo-2.png');
  const mobilehelpLogo = getImage('mobilehelp-logo-2.png');
  const adtHealthLogo = getImage('adt-health-logo-web-2.png');
  
  // Get focus section image
  const focusImage = getImage('PriceCompareImage-1.jpg');
  const warningImage = getImage('PriceCompareImage-2.jpg');

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const toggleWarning = (index: number) => {
    setOpenWarningIndex(openWarningIndex === index ? null : index);
  };

  const faqItems = [
    {
      question: "How do medical alert systems work?",
      answer: (
        <>
          When a senior presses the medical alert button on his/her necklace or wristband, the medical alert system automatically calls our Command Center. We recommend optional, built-in fall detection for the highest level of security, as it senses falls and automatically calls the Alert1 Command Center. From there, a trained emergency dispatcher will come over the two-way speaker and ask if you're ok. Based on your response, they will call your Circle of Care (personal contacts), emergency services, or both. If you're unresponsive, they will call emergency services immediately, and then your Circle of Care. Tip: Check out our <Link to="/how-it-works" style={{ color: '#0066cc', textDecoration: 'underline' }}>How Medical Alert Systems Work page</Link> for more details.
        </>
      )
    },
    {
      question: "Does the mobile medical alert system with GPS work anywhere?",
      answer: (
        <>
          Yes. The mobile medical alert system with GPS works anywhere in the continental United States. <Link to="/where-it-works" style={{ color: '#0066cc', textDecoration: 'underline' }}>Check our coverage page</Link> for more information.
        </>
      )
    },
    {
      question: "How much do medical alert systems cost?",
      answer: "Alert1 medical alert systems are free to use, and our 24/7 monitoring fees range between $20 and $55 per month, depending on the features and functions you desire. Some competitors charge more than three times that amount! We are the ideal choice for anyone looking for an affordable medical alert. Alert1 provides some of the best value in the industry."
    },
    {
      question: "If I select an annual plan and something happens and I need to cancel, do I get a refund?",
      answer: "If you are on a contract of a specified length (e.g. annual) for a discount, you may cancel early but Alert1 does not offer refunds unless the member has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed. If you are not on a long-term plan, any user may cancel service at any time by returning the equipment."
    },
    {
      question: "Once an alert is sent, who gets called?",
      answer: "Once a member presses their button alarm, a trained emergency dispatcher will come over the two-way speaker and ask if they are ok. Based on the response, the agent will call the Circle of Care (the personal contacts that the member designates in advance), emergency services, or both. If the member is unresponsive, the agent will call emergency services immediately, and then the member's Circle of Care."
    }
  ];

  return (
    <div className="compare-medical-alert-systems-page">
      {/* Hero Section */}
      <section className="compare-medical-alert-hero-section">
        <div className="compare-medical-alert-hero-container">
          <h1 className="compare-medical-alert-hero-title">
            The Best Medical Alert Systems for 2025
          </h1>
          
          <p className="compare-medical-alert-hero-text">
            Finding the right medical alert system for seniors or "personal help button" can be difficult. There are lots of choices available and the costs of medical alerts can be prohibitive. We understand your need to research before you choose the best medical alert system for you. Our medical alert comparison hub will give you the information and pricing needed to help you make a decision. With free equipment use and some of the lowest monitoring rates, we're confident you'll find that <strong>Alert1 offers the best value in the industry today</strong>.
          </p>
          
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/medical-alerts" className="compare-medical-alert-view-btn">
              VIEW SYSTEMS
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ/Dropdown Menu Section with YouTube Video */}
      <section className="compare-medical-alert-faq-section">
        <div className="compare-medical-alert-faq-container">
          <div className="compare-medical-alert-faq-wrapper">
            <div className="compare-medical-alert-faq-list">
              {faqItems.map((item, index) => (
                <div key={index} className="compare-medical-alert-faq-item">
                  <button 
                    className="compare-medical-alert-faq-question"
                    onClick={() => toggleFaq(index)}
                  >
                    <span>{item.question}</span>
                    <span className="compare-medical-alert-faq-icon">{openFaqIndex === index ? '−' : '+'}</span>
                  </button>
                  {openFaqIndex === index && (
                    <div className="compare-medical-alert-faq-answer">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="compare-medical-alert-video-container">
              <iframe
                src="https://player.vimeo.com/video/566727092"
                width="100%"
                height="315"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                style={{ borderRadius: '8px' }}
                title="Vimeo video player"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="compare-medical-alert-comparison-section compare-medical-alert-section-white">
        <div className="compare-medical-alert-comparison-container">
          <h2 className="compare-medical-alert-comparison-title">
            See How Alert1 Measures up Against Other Medical Alert System Alternatives.
          </h2>
          
          <div className="compare-medical-alert-comparison-grid">
            {/* Life Alert */}
            <Link to="/pricing/compare-alert1-to-life-alert" className="compare-medical-alert-comparison-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="compare-medical-alert-comparison-header">
                <div className="compare-medical-alert-logo-wrapper">
                  {alert1Logo && (
                    <img src={alert1Logo} alt="Alert1" className="compare-medical-alert-logo" />
                  )}
                </div>
                <span className="compare-medical-alert-vs">vs</span>
                <div className="compare-medical-alert-competitor-logo-wrapper">
                  {lifeAlertLogo && (
                    <img src={lifeAlertLogo} alt="Life Alert" className="compare-medical-alert-competitor-logo" />
                  )}
                </div>
              </div>
              <div className="compare-medical-alert-comparison-divider"></div>
              <p className="compare-medical-alert-comparison-text">
                Life Alert costs can be significantly higher than Alert1's medical alert system costs
              </p>
            </Link>

            {/* Bay Alarm Medical */}
            <Link to="/pricing/compare-alert1-to-bay-alarm" className="compare-medical-alert-comparison-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="compare-medical-alert-comparison-header">
                <div className="compare-medical-alert-logo-wrapper">
                  {alert1Logo && (
                    <img src={alert1Logo} alt="Alert1" className="compare-medical-alert-logo" />
                  )}
                </div>
                <span className="compare-medical-alert-vs">vs</span>
                <div className="compare-medical-alert-competitor-logo-wrapper">
                  {bayAlarmLogo && (
                    <img src={bayAlarmLogo} alt="Bay Alarm Medical" className="compare-medical-alert-competitor-logo" />
                  )}
                </div>
              </div>
              <div className="compare-medical-alert-comparison-divider"></div>
              <p className="compare-medical-alert-comparison-text">
                Reviews of medical alert systems vary widely. Go with the pros
              </p>
            </Link>

            {/* Medical Guardian */}
            <Link to="/pricing/compare-alert1-to-medical-guardian" className="compare-medical-alert-comparison-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="compare-medical-alert-comparison-header">
                <div className="compare-medical-alert-logo-wrapper">
                  {alert1Logo && (
                    <img src={alert1Logo} alt="Alert1" className="compare-medical-alert-logo" />
                  )}
                </div>
                <span className="compare-medical-alert-vs">vs</span>
                <div className="compare-medical-alert-competitor-logo-wrapper">
                  {medicalGuardianLogo && (
                    <img src={medicalGuardianLogo} alt="Medical Guardian" className="compare-medical-alert-competitor-logo" />
                  )}
                </div>
              </div>
              <div className="compare-medical-alert-comparison-divider"></div>
              <p className="compare-medical-alert-comparison-text">
                Get an easier to use medical alert system with great service
              </p>
            </Link>

            {/* GreatCall */}
            <Link to="/pricing/compare-alert1-to-greatcall" className="compare-medical-alert-comparison-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="compare-medical-alert-comparison-header">
                <div className="compare-medical-alert-logo-wrapper">
                  {alert1Logo && (
                    <img src={alert1Logo} alt="Alert1" className="compare-medical-alert-logo" />
                  )}
                </div>
                <span className="compare-medical-alert-vs">vs</span>
                <div className="compare-medical-alert-competitor-logo-wrapper">
                  {greatcallLogo && (
                    <img src={greatcallLogo} alt="GreatCall" className="compare-medical-alert-competitor-logo" />
                  )}
                </div>
              </div>
              <div className="compare-medical-alert-comparison-divider"></div>
              <p className="compare-medical-alert-comparison-text">
                Only pay for what you need. Don't pay for what you don't
              </p>
            </Link>

            {/* MobileHelp */}
            <Link to="/pricing/compare-alert1-to-mobilehelp" className="compare-medical-alert-comparison-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="compare-medical-alert-comparison-header">
                <div className="compare-medical-alert-logo-wrapper">
                  {alert1Logo && (
                    <img src={alert1Logo} alt="Alert1" className="compare-medical-alert-logo" />
                  )}
                </div>
                <span className="compare-medical-alert-vs">vs</span>
                <div className="compare-medical-alert-competitor-logo-wrapper">
                  {mobilehelpLogo && (
                    <img src={mobilehelpLogo} alt="MobileHelp" className="compare-medical-alert-competitor-logo" />
                  )}
                </div>
              </div>
              <div className="compare-medical-alert-comparison-divider"></div>
              <p className="compare-medical-alert-comparison-text">
                Don't let limited offerings or high prices hold you back
              </p>
            </Link>

            {/* ADT Health */}
            <Link to="/pricing/compare-alert1-to-adt-health" className="compare-medical-alert-comparison-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="compare-medical-alert-comparison-header">
                <div className="compare-medical-alert-logo-wrapper">
                  {alert1Logo && (
                    <img src={alert1Logo} alt="Alert1" className="compare-medical-alert-logo" />
                  )}
                </div>
                <span className="compare-medical-alert-vs">vs</span>
                <div className="compare-medical-alert-competitor-logo-wrapper">
                  {adtHealthLogo && (
                    <img src={adtHealthLogo} alt="ADT Health" className="compare-medical-alert-competitor-logo" />
                  )}
                </div>
              </div>
              <div className="compare-medical-alert-comparison-divider"></div>
              <p className="compare-medical-alert-comparison-text">
                A dedicated medical alert system for seniors vs. an alarm company
              </p>
            </Link>
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to="/medical-alerts" className="compare-medical-alert-view-btn">
              VIEW SYSTEMS
            </Link>
          </div>
        </div>
      </section>

      {/* Focus Section */}
      <section className="compare-medical-alert-focus-section compare-medical-alert-section-gray">
        <div className="compare-medical-alert-focus-container">
          <div className="compare-medical-alert-focus-content">
            <h2 className="compare-medical-alert-focus-title">
              Focus on What Matters Most
            </h2>
            
            <ul className="compare-medical-alert-focus-list">
              <li>Get the best medical alert system for seniors and their families</li>
              <li>Pay for what you need and cancel when you want with no penalty and no questions asked</li>
              <li>Enjoy best in class customer support</li>
              <li>Compare to the cost for Life Alert. See if you can get a better value</li>
              <li>Rest easy, knowing our Command Centers offer 24/7/365 protection</li>
              <li>Enjoy super-fast 2 - 3 day express shipping</li>
            </ul>
          </div>
          
          <div className="compare-medical-alert-focus-image">
            <div className="circular-image-wrapper">
              {focusImage ? (
                <img 
                  src={focusImage} 
                  alt="Grandmother and granddaughter baking together" 
                  className="circular-image"
                />
              ) : (
                <div className="circular-image-placeholder" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs Section */}
      <section className="compare-medical-alert-warning-section compare-medical-alert-section-white">
        <div className="compare-medical-alert-warning-container">
          <div className="compare-medical-alert-warning-wrapper">
            <div className="compare-medical-alert-warning-image">
              {warningImage && (
                <img 
                  src={warningImage} 
                  alt="Warning signs" 
                  className="compare-medical-alert-warning-img"
                />
              )}
            </div>
            
            <div className="compare-medical-alert-warning-content">
              <h2 className="compare-medical-alert-warning-title">
                7 Warning Signs to Look Out for<br />when Comparing Medical Alert Systems
              </h2>
              <p className="compare-medical-alert-warning-subtitle">
                See how Alert1 measures up against other medical alert system alternatives
              </p>
              
              <div className="compare-medical-alert-warning-list">
                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(0)}
                  >
                    <span>1. Long term contracts</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 0 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 0 && (
                    <div className="compare-medical-alert-warning-answer">
                      Alert1 is pleased to offer options to suit every budget, need, and lifestyle. We recommend longer term plans at discounted monthly rates for those who are looking for the best savings. For those who prefer short-term plans, we offer monthly subscriptions as well. Some of our competitors insist on "locking" their members into multi-year plans, but at Alert1, the choice is all yours.
                    </div>
                  )}
                </div>

                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(1)}
                  >
                    <span>2. Quality of emergency response</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 1 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 1 && (
                    <div className="compare-medical-alert-warning-answer">
                      Did you know that not all medical alert companies' emergency operators stay on the line with you or your loved one until help arrives? At Alert1, we understand how frightening, painful, and disorienting an accident or fall could be and our US-based Command Center responders stay on the line with our members until appropriate care has been secured. With Alert1, you will never face an emergency alone.
                    </div>
                  )}
                </div>

                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(2)}
                  >
                    <span>3. The medical alert devices haven't changed in years</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 2 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 2 && (
                    <div className="compare-medical-alert-warning-answer">
                      At Alert1, we think you'll discover that these aren't your Grandmother's medical alert systems. We offer the latest technology such as built-in Fall Detection and GPS—all contained within a small, lightweight device that can be worn as a pendant, on a wristband, on a belt clip, or even in your pocket. We also offer the latest accessories for those who are fashion-forward.
                    </div>
                  )}
                </div>

                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(3)}
                  >
                    <span>4. Bad customer reviews</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 3 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 3 && (
                    <div className="compare-medical-alert-warning-answer">
                      Alert1 is proud of our stellar reputation amongst our members. We have over 1300 reviews and an average of 5 out of 5 stars. <Link to="/reviews" style={{ color: '#0066cc', textDecoration: 'underline' }}>See our reviews here</Link>.
                    </div>
                  )}
                </div>

                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(4)}
                  >
                    <span>5. Usage charges or false alarm fees</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 4 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 4 && (
                    <div className="compare-medical-alert-warning-answer">
                      Alert1 never assesses additional fees for multiple button pushes or false alarms. You may find that other providers penalize their members with these fees.
                    </div>
                  )}
                </div>

                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(5)}
                  >
                    <span>6. Hidden fees</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 5 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 5 && (
                    <div className="compare-medical-alert-warning-answer">
                      Alert1 is very transparent with our pricing. It is visible on our website and there are no surprise fees.
                    </div>
                  )}
                </div>

                <div className="compare-medical-alert-warning-item">
                  <button 
                    className="compare-medical-alert-warning-question"
                    onClick={() => toggleWarning(6)}
                  >
                    <span>7. US-based service</span>
                    <span className="compare-medical-alert-warning-icon">{openWarningIndex === 6 ? '−' : '+'}</span>
                  </button>
                  {openWarningIndex === 6 && (
                    <div className="compare-medical-alert-warning-answer">
                      Alert1 provides service across the nation, and is proudly headquartered right here in the USA. Our Command Center emergency agents are also based here in the US. We do not outsource any of our services.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="compare-medical-alert-cta-section compare-medical-alert-section-gray">
        <div className="compare-medical-alert-cta-container">
          <h2 className="compare-medical-alert-cta-title">
            Want to See the Difference for Yourself?
          </h2>
          <Link to="/medical-alerts" className="compare-medical-alert-cta-btn">
            TRY IT TODAY
          </Link>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="compare-medical-alert-features-section compare-medical-alert-section-white">
        <div className="compare-medical-alert-features-container">
          <h2 className="compare-medical-alert-features-title">
            Additional Features to Consider When Choosing a Medical Alert System
          </h2>

          <div className="compare-medical-alert-features-intro">
            <h3 className="compare-medical-alert-features-subtitle">Key Features That Provide Peace of Mind</h3>
            <p className="compare-medical-alert-features-intro-text">
              When comparing medical alert systems like Alert1, Bay Alarm Medical, and Medical Guardian, consider these important features that provide peace of mind for you and your family members:
            </p>
          </div>

          <div className="compare-medical-alert-features-content">
            <div className="compare-medical-alert-feature-group">
              <h4 className="compare-medical-alert-feature-group-title">In-Home Systems</h4>
              <ul className="compare-medical-alert-feature-list">
                <li>In-home base unit with backup battery for power outage protection</li>
                <li>Home system range that covers your entire living space</li>
                <li>Battery backup that lasts 24-72 hours depending on the monitored system</li>
              </ul>
            </div>

            <div className="compare-medical-alert-feature-group">
              <h4 className="compare-medical-alert-feature-group-title">Mobile Systems for On-the-Go Protection</h4>
              <ul className="compare-medical-alert-feature-list">
                <li>Mobile GPS devices for location tracking outside the home</li>
                <li>On-the-go systems that work anywhere with cellular network coverage</li>
                <li>Mobile and smartwatch systems with GPS tracking capabilities</li>
                <li>Wearable devices that are water resistant for shower safety</li>
                <li>Medical alert smartwatch options for tech-savvy seniors</li>
              </ul>
            </div>

            <div className="compare-medical-alert-feature-group">
              <h4 className="compare-medical-alert-feature-group-title">Monitoring Center Quality</h4>
              <ul className="compare-medical-alert-feature-list">
                <li>US-based monitoring center with trained emergency personnel</li>
                <li>Response protocols for contacting emergency contacts</li>
                <li>Option to customize your emergency contact list</li>
              </ul>
            </div>

            <div className="compare-medical-alert-feature-group">
              <h4 className="compare-medical-alert-feature-group-title">Health and Safety Features</h4>
              <ul className="compare-medical-alert-feature-list">
                <li>Fall detection devices with advanced sensors (results from fall detection tests vary by company)</li>
                <li>Medication reminders through mobile apps or base units</li>
                <li>Support for those with chronic health issues</li>
              </ul>
            </div>

            <div className="compare-medical-alert-feature-group">
              <h4 className="compare-medical-alert-feature-group-title">Cost Considerations</h4>
              <ul className="compare-medical-alert-feature-list">
                <li>Monthly fee structures ranging from $20-$60</li>
                <li>Affordable pricing options with annual plans</li>
                <li>Monthly service fee inclusions and limitations</li>
                <li>Potential additional monthly fee for premium features</li>
                <li>Presence of activation fees or equipment fees</li>
                <li>Cancellation fees policies (Alert1 has none)</li>
              </ul>
            </div>

            <div className="compare-medical-alert-feature-group">
              <h4 className="compare-medical-alert-feature-group-title">What Consumer Reports and Experts Say</h4>
              <p className="compare-medical-alert-feature-text">
                Most tested medical alert systems show that quality varies significantly between providers. Only companies with reliable technical support and responsive customer service should make your shortlist.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* System Comparison Guide Section */}
      <section className="compare-medical-alert-guide-section compare-medical-alert-section-gray">
        <div className="compare-medical-alert-guide-container">
          <h2 className="compare-medical-alert-guide-title">System Comparison Guide</h2>
          
          <div className="compare-medical-alert-guide-table-wrapper">
            <table className="compare-medical-alert-guide-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="compare-medical-alert-guide-highlight">Alert1</th>
                  <th>Bay Alarm</th>
                  <th>Medical Guardian</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Monthly cost</td>
                  <td className="compare-medical-alert-guide-highlight">$20-$45</td>
                  <td>Varies</td>
                  <td>Varies</td>
                </tr>
                <tr>
                  <td>Battery life ranges</td>
                  <td className="compare-medical-alert-guide-highlight">24-72 hrs</td>
                  <td>Varies</td>
                  <td>Varies</td>
                </tr>
                <tr>
                  <td>Fall detection</td>
                  <td className="compare-medical-alert-guide-highlight">Optional</td>
                  <td>Available</td>
                  <td>Available</td>
                </tr>
                <tr>
                  <td>Unmonitored systems</td>
                  <td className="compare-medical-alert-guide-highlight">No</td>
                  <td>No</td>
                  <td>No</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="compare-medical-alert-guide-text">
            When facing a medical emergency, you need a system that works flawlessly. Most systems from reputable providers will connect you quickly to help during an emergency alert situation.
          </p>

          <div className="compare-medical-alert-guide-balance">
            <h3 className="compare-medical-alert-guide-balance-title">Finding the Right Balance</h3>
            <p className="compare-medical-alert-guide-balance-text">
              The best medical alert system balances features and cost. While some providers offer more key features, they may charge a higher monthly fee. Consider what matters most for your specific situation when making this important decision that will provide peace of mind for you and your loved ones.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/medical-alerts" className="compare-medical-alert-view-btn">
              VIEW SYSTEMS
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompareMedicalAlertSystemsPage;
